<template>
  <div class="anticipations-table">
    <!-- Selection Summary -->
    <div v-if="selectionEnabled && selectedItems.length > 0" class="selection-summary">
      <div class="summary-item">
        <span class="summary-label">{{ t('Selecionados') }}:</span>
        <span class="summary-value">{{ selectedItems.length }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">{{ t('Valor Liquido Total') }}:</span>
        <span class="summary-value">{{ n(selectedNetTotal, 'currency') }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-actions">
        <Button
          data-testid="anticipations-batch-sync-button"
          @click="handleBatchSync"
          :disabled="!canBatchSync"
          severity="secondary"
          size="small"
          :label="t('Sincronizar Selecionados')"
          icon="pi pi-sync"
        />
        <Button
          data-testid="anticipations-clear-selection-button"
          @click="clearSelection"
          severity="secondary"
          size="small"
          :label="t('Limpar Seleção')"
          icon="pi pi-times"
          text
        />
      </div>
    </div>

    <DataTable
      ref="dataTableRef"
      v-model:selection="selectedItems"
      :key="tableKey"
      display-mode="handle_user_device"
      :config="tableConfigComputed"
      :data="enrichedAnticipations"
      :filterConfig="filterConfig"
      :auto-load="false"
      :refreshButton="{ show: true }"
      @refresh="$emit('refresh')"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @selection-change="handleSelectionChange" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import type { DataTableConfig } from '../../grid/types';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useAnticipations } from '../../composables/useAnticipations';

type Id = number | string;

interface AnticipationItem {
  id?: Id;
  status?: string;
  total_value?: number;
  anticipation_value?: number;
  fee?: number;
  net_value?: number;
  anticipation_days?: number;
  request_date?: string;
  anticipation_date?: string;
  due_date?: string;
  provider_anticipation_id?: string;
  [key: string]: unknown;
}

interface EnrichedAnticipation extends AnticipationItem {
  total_value_display: number;
  anticipation_value_display: number;
  fee_display: number;
  net_value_display: number;
}

interface CellParams {
  value: unknown;
  data?: AnticipationItem;
}

interface ButtonClickPayload {
  value: Id;
  data?: AnticipationItem;
  action: string;
}

interface DataTableInstance {
  clearSelection: () => void;
}

const props = withDefaults(
  defineProps<{
    anticipations?: AnticipationItem[];
    loading?: boolean;
    selectionEnabled?: boolean;
  }>(),
  {
    anticipations: () => [],
    loading: false,
    selectionEnabled: true,
  }
);

const emit = defineEmits<{
  (e: 'refresh'): void;
  (e: 'view-anticipation', id: Id): void;
  (e: 'cancel-anticipation', id: Id): void;
  (e: 'sync-anticipation', id: Id): void;
  (e: 'batch-sync', ids: Id[]): void;
  (e: 'selection-change', selection: AnticipationItem[]): void;
}>();

const { t, n } = useI18n();
const { getStatusConfig, isFinalStatus } = useAnticipations();

// Refs
const dataTableRef = ref<DataTableInstance | null>(null);
const tableKey = ref<number>(0);
const selectedItems = ref<AnticipationItem[]>([]);

// Computed: Selected net total value
const selectedNetTotal = computed<number>(() => {
  return selectedItems.value.reduce((sum, item) => {
    // net_value is in cents
    return sum + ((item.net_value || 0) / 100);
  }, 0);
});

// Computed: Can batch sync (only non-final status anticipations)
const canBatchSync = computed<boolean>(() => {
  if (selectedItems.value.length === 0) return false;
  return selectedItems.value.some(item => !isFinalStatus(item.status));
});

// Computed: Items that can be synced from selection
const syncableItems = computed<AnticipationItem[]>(() => {
  return selectedItems.value.filter(item => !isFinalStatus(item.status));
});

// Watch for anticipations changes to clear selection
watch(() => props.anticipations, () => {
  selectedItems.value = [];
}, { deep: true });

// Status severity mapping for badges
const getStatusSeverityClass = (status?: string): string => {
  const severityMap: Record<string, string> = {
    'PENDING': 'warn',
    'SCHEDULED': 'info',
    'CREDITED': 'success',
    'DEBITED': 'info',
    'DENIED': 'danger',
    'CANCELLED': 'secondary',
    'OVERDUE': 'danger'
  };
  return severityMap[status || ''] || 'secondary';
};

// Computed: Enriched anticipations data
const enrichedAnticipations = computed<EnrichedAnticipation[]>(() => {
  return props.anticipations.map(ant => {
    return {
      ...ant,
      // Convert cents to reais for display
      total_value_display: (ant.total_value || 0) / 100,
      anticipation_value_display: (ant.anticipation_value || 0) / 100,
      fee_display: (ant.fee || 0) / 100,
      net_value_display: (ant.net_value || 0) / 100,
    };
  });
});

// Render functions
const renderStatus = (params: CellParams): HTMLElement => {
  const status = params.value as string | undefined;
  const config = getStatusConfig(status) as { icon: string; label: string };
  const severityClass = getStatusSeverityClass(status);

  const span = document.createElement('span');
  span.className = `status-badge status-${severityClass}`;
  span.innerHTML = `<i class="pi ${config.icon}"></i> ${t(config.label)}`;
  return span;
};

// Table configuration
const tableConfigComputed = computed<DataTableConfig<Record<string, unknown>>>(() => ({
  tableId: 'anticipationsDataTable',
  checkbox: props.selectionEnabled ? {
    enabled: true,
    position: 'first',
    width: '3rem'
  } : { enabled: false },
  columns: [
    {
      data: 'id',
      title: t('ID'),
      footerType: 'count',
      renderType: 'clickable_id'
    },
    {
      data: 'status',
      title: t('Status'),
      cellRenderer: renderStatus
    },
    {
      data: 'total_value_display',
      title: t('Valor Total'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'fee_display',
      title: t('Taxa'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'net_value_display',
      title: t('Valor Liquido'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'anticipation_days',
      title: t('Dias Antecipados'),
      renderType: 'number'
    },
    {
      data: 'request_date',
      title: t('Data Solicitacao'),
      renderType: 'date',
      dataType: 'date'
    },
    {
      data: 'anticipation_date',
      title: t('Data Credito'),
      renderType: 'date',
      dataType: 'date'
    },
    {
      data: 'due_date',
      title: t('Vencimento Original'),
      renderType: 'date',
      dataType: 'date'
    },
    {
      data: 'provider_anticipation_id',
      title: t('ID Provedor')
    },
    {
      data: 'id',
      title: t('Sincronizar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Sincronizar'),
      buttonIcon: 'pi pi-sync',
      buttonClass: 'p-button-secondary p-button-sm',
      buttonAction: 'sync',
      buttonCondition: (rowData: AnticipationItem) => !isFinalStatus(rowData.status)
    },
    {
      data: 'id',
      title: t('Cancelar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Cancelar'),
      buttonIcon: 'pi pi-times',
      buttonClass: 'p-button-danger p-button-sm',
      buttonAction: 'cancel',
      buttonCondition: (rowData: AnticipationItem) => rowData.status === 'PENDING' || rowData.status === 'SCHEDULED'
    }
  ],
  pagination: true,
  pageLength: 25,
  filterDisplay: 'row',
  heightMode: 'fill-viewport',
  footerLabel: t('Totais'),
  rowTestId: (row: Record<string, unknown>) => {
    const id = row['id'];
    return id == null ? '' : `anticipations-row-${id}`;
  }
}));

// Filter configuration
const filterConfig = computed(() => ({
  columns: {
    'id': 'text',
    'status': 'select',
    'total_value_display': 'number',
    'fee_display': 'number',
    'net_value_display': 'number',
    'anticipation_days': 'number',
    'request_date': 'date',
    'anticipation_date': 'date',
    'due_date': 'date'
  }
}));

// Methods
const handleRowClick = (event?: { data?: AnticipationItem }): void => {
  log('AnticipationsTable', 'handleRowClick', 'Row clicked', event?.data?.id || null);
};

const handleEditClick = (anticipationId: Id): void => {
  log('AnticipationsTable', 'handleEditClick', 'Viewing anticipation', anticipationId);
  emit('view-anticipation', anticipationId);
};

const handleButtonClick = (payload?: ButtonClickPayload): void => {
  if (!payload) return;
  const { value, action } = payload;
  log('AnticipationsTable', 'handleButtonClick', 'Button clicked', { value, action });

  switch (action) {
    case 'cancel':
      emit('cancel-anticipation', value);
      break;
    case 'sync':
      emit('sync-anticipation', value);
      break;
    default:
      log('AnticipationsTable', 'handleButtonClick', 'Unknown button action', action);
  }
};

// Selection handlers
const handleSelectionChange = (payload?: { selection: any[]; added: any[]; removed: any[]; }): void => {
  const selection = payload?.selection || [];
  log('AnticipationsTable', 'handleSelectionChange', 'Selection changed', { count: selection.length });
  emit('selection-change', selection as AnticipationItem[]);
};

const handleBatchSync = (): void => {
  const itemsToSync = syncableItems.value;
  log('AnticipationsTable', 'handleBatchSync', 'Batch sync requested', {
    selectedCount: selectedItems.value.length,
    syncableCount: itemsToSync.length
  });

  if (itemsToSync.length === 0) {
    eventBus.emit('globalMessage.warning', t('Nenhuma antecipacao selecionada pode ser sincronizada'));
    return;
  }

  const anticipationIds = itemsToSync.map(item => item.id).filter((v): v is Id => v !== undefined);
  emit('batch-sync', anticipationIds);
};

const clearSelection = (): void => {
  log('AnticipationsTable', 'clearSelection', 'Clearing selection');
  selectedItems.value = [];
};

// Expose methods for parent components
const forceRefresh = (): void => {
  tableKey.value += 1;
};

const getSelectedItems = (): AnticipationItem[] => {
  return selectedItems.value;
};

const setSelection = (items: AnticipationItem[]): void => {
  selectedItems.value = items;
};

defineExpose({
  forceRefresh,
  getSelectedItems,
  setSelection,
  clearSelection
});
</script>

<style scoped>
.anticipations-table {
  width: 100%;
}

/* Selection Summary */
.selection-summary {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: var(--p-blue-50, var(--surface-50));
  border-radius: 8px;
  border: 1px solid var(--p-blue-200, var(--surface-border));
}

.summary-item {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-blue-700, var(--text-color));
}

.summary-divider {
  width: 1px;
  height: 24px;
  background: var(--p-blue-200, var(--surface-border));
}

.summary-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

/* Status badge styles */
:deep(.status-badge) {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

:deep(.status-badge i) {
  font-size: 0.7rem;
}

:deep(.status-info) {
  background-color: var(--p-blue-100);
  color: var(--p-blue-700);
}

:deep(.status-success) {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}

:deep(.status-warn) {
  background-color: var(--p-yellow-100);
  color: var(--p-yellow-700);
}

:deep(.status-danger) {
  background-color: var(--p-red-100);
  color: var(--p-red-700);
}

:deep(.status-secondary) {
  background-color: var(--p-surface-200);
  color: var(--p-surface-600);
}
</style>
